import type { RenderObject, TableScene, Vec3 } from '@v2/contracts'
import { differentPose, interpolatePose, poseOf, shufflePackets, type PiecePose } from './motion'

interface Motion {
  readonly from: PiecePose
  readonly start: number
  readonly duration: number
  readonly lift: number
  readonly queued?: boolean
  readonly departure?: boolean
}
interface Shuffle { readonly start: number }
interface AnonymousDeal extends Motion { readonly target: string; readonly id: string }
export interface TableAnimationContext {
  /** Only the current viewer's projection; never authoritative hidden state. */
  readonly scene?: TableScene
  /** Compatibility fallback for scenes that have not authored drawFrom. */
  readonly source?: Vec3
}

const SHUFFLE_SECONDS = 0.96
const DEAL_INTERVAL = 0.075

/**
 * Display transforms are disposable. Rules commit immediately and never wait for
 * an effect. Only poses survive a projection update; faces always come from the
 * latest projection, including while a visible piece becomes hidden.
 */
export class TableAnimations {
  private objects: readonly RenderObject[] = []
  private motions = new Map<string, Motion>()
  private shuffles = new Map<string, Shuffle>()
  private shuffleKeys = new Map<string, string | number>()
  private zones = new Map<string, string>()
  private counts = new Map<string, number>()
  private deals: AnonymousDeal[] = []
  private serial = 0

  update(objects: readonly RenderObject[], now: number, baseline = false, context: TableAnimationContext | Vec3 = {}): void {
    const options: TableAnimationContext = Array.isArray(context) ? { source: context as Vec3 } : context as TableAnimationContext
    const previousTargets = new Map(this.objects.map(object => [object.id, object]))
    const prior = new Map(this.sample(now).map(object => [object.id, object]))
    const targets = new Map(objects.map(object => [object.id, object]))
    const pieces = new Map(options.scene?.pieces.map(piece => [piece.id, piece]))
    const zones = new Map(options.scene?.zones.map(zone => [zone.id, zone]))

    if (baseline) {
      this.motions.clear()
      this.shuffles.clear()
      this.shuffleKeys.clear()
      this.deals = []
      this.serial = 0
    }
    for (const id of this.shuffles.keys()) if (!targets.has(id)) this.shuffles.delete(id)
    for (const piece of pieces.values()) {
      const key = piece.motion?.shuffleKey
      if (key === undefined) continue
      const previous = this.shuffleKeys.get(piece.id)
      if (!baseline && previous !== undefined && previous !== key && targets.get(piece.id)?.shape === 'card') {
        this.shuffles.set(piece.id, { start: now })
      }
      // Retain the last occurrence even if a proxy briefly disappears. A refresh
      // starts at a baseline; a repeated room-status projection never replays it.
      this.shuffleKeys.set(piece.id, key)
    }

    const nextMotions = new Map<string, Motion>()
    const dealt = new Map<string, number>()
    if (!baseline) for (const object of objects) {
      const piece = pieces.get(object.id)
      // Surfaces stay below their contents, and anonymous decorative deck layers
      // follow their shuffle packets rather than behaving as independently dealt cards.
      // Cubes share beveled slab geometry but are movable physical components.
      if (object.shape === 'board' || (object.shape === 'slab' && piece?.kind !== 'cube') || object.id.startsWith('__table:')) continue
      const old = prior.get(object.id)
      const previousTarget = previousTargets.get(object.id)
      const zone = piece && zones.get(piece.zone)
      const sourceId = piece?.motion?.from ?? zone?.drawFrom
      const shuffle = sourceId ? this.shuffles.get(sourceId) : undefined
      const source = sourceId ? shuffle ? targets.get(sourceId) ?? prior.get(sourceId) : prior.get(sourceId) ?? targets.get(sourceId) : undefined
      const shuffleDelay = shuffle ? Math.max(0, shuffle.start + SHUFFLE_SECONDS - now) : 0
      const anonymousPile = !!(piece?.hidden && piece.count !== undefined && source && sourceId !== object.id)
      if (anonymousPile) {
        const gain = Math.max(0, (piece!.count ?? 0) - (this.counts.get(object.id) ?? 0))
        for (let index = 0; index < Math.min(8, gain); index++) {
          this.deals.push({
            id: `__table:deal:${this.serial++}`,
            target: object.id,
            from: poseOf(source!),
            start: now + shuffleDelay + index * DEAL_INTERVAL,
            duration: 0.68,
            lift: 0.8,
          })
        }
      }
      const changedZone = piece !== undefined && this.zones.has(piece.id) && this.zones.get(piece.id) !== piece.zone
      const existing = this.motions.get(object.id)
      const pileCountChanged = piece?.hidden && piece.count !== undefined && this.counts.has(object.id)
        && this.counts.get(object.id) !== piece.count
      const countOnlyPile = pileCountChanged && previousTarget && !changedZone
        && [0, 2].every(index => Math.abs(previousTarget.position[index] - object.position[index]) < 0.001
          && Math.abs(previousTarget.size[index] - object.size[index]) < 0.001)
        && !differentPose({ ...previousTarget, position: object.position, size: object.size }, object)
      if (countOnlyPile) {
        // A thinner deck is still resting on the table. Its count does not make
        // the whole pile jump, even while anonymous cards arrive above it.
        if (existing) nextMotions.set(object.id, existing)
        continue
      }
      if (existing && previousTarget && !changedZone && !differentPose(previousTarget, object)) {
        nextMotions.set(object.id, existing)
        continue
      }

      if (!old && anonymousPile) continue
      // A known discard card may return in a newly shuffled hand with the same
      // identity. It belongs to the same deal as its previously hidden neighbors.
      const shuffledDeal = !!(changedZone && source && shuffle && object.shape === 'card')
      const from = shuffledDeal ? poseOf(source!) : old ? poseOf(old) : source ? poseOf(source)
        : object.shape === 'card' && options.source ? { ...poseOf(object), position: options.source } : undefined
      if (!from || (!changedZone && !differentPose(from, object))) continue
      const newDeal = (!old || shuffledDeal) && object.shape === 'card'
      const group = sourceId ?? '__fallback'
      const ordinal = newDeal ? dealt.get(group) ?? 0 : 0
      if (newDeal) dealt.set(group, ordinal + 1)
      const distance = Math.hypot(...object.position.map((value, index) => value - from.position[index]))
      const departure = old?.layer === 'foreground' && object.layer !== 'foreground'
      nextMotions.set(object.id, {
        from,
        start: now + (newDeal ? shuffleDelay + (piece?.motion?.delayMs !== undefined ? Math.min(9000, Math.max(0, piece.motion.delayMs)) / 1000 : Math.min(ordinal, 9) * DEAL_INTERVAL) : 0),
        duration: piece?.motion?.durationMs !== undefined ? Math.min(5000, Math.max(100, piece.motion.durationMs)) / 1000 : newDeal ? 0.68 : departure ? 0.62 : 0.48,
        lift: Math.min(1.5, 0.35 + distance * 0.055),
        queued: newDeal,
        departure,
      })
    }
    this.objects = objects
    this.motions = nextMotions
    this.zones = new Map(options.scene?.pieces.map(piece => [piece.id, piece.zone]))
    this.counts = new Map(options.scene?.pieces.map(piece => [piece.id, piece.count ?? 0]))
    this.deals = this.deals.filter(deal => targets.has(deal.target) && now < deal.start + deal.duration)
  }

  /**
   * Camera-relative hand poses may change every frame. Supplying destinations
   * follows those poses without starting an event or extending its lifetime.
   */
  sample(now: number, destinations?: readonly RenderObject[]): readonly RenderObject[] {
    if (destinations) this.objects = destinations
    if (!this.pending) return this.objects
    const objects: RenderObject[] = []
    for (const [id, shuffle] of this.shuffles) {
      if (now - shuffle.start >= SHUFFLE_SECONDS) this.shuffles.delete(id)
    }
    const shufflingStacks = [...this.shuffles.keys()].map(id => `__table:stack:${id}:`)
    for (const destination of this.objects) {
      if (shufflingStacks.some(prefix => destination.id.startsWith(prefix))) continue
      const motion = this.motions.get(destination.id)
      let object = destination
      if (motion) {
        // Delayed cards are still in the back-only source pile. Rendering the
        // hand's foreground face here would cover the shuffle before departure.
        if (motion.queued && now < motion.start) continue
        const progress = Math.max(0, Math.min(1, (now - motion.start) / motion.duration))
        if (progress === 1) this.motions.delete(destination.id)
        else object = {
          ...destination,
          ...interpolatePose(motion.from, poseOf(destination), progress, motion.lift, motion.departure),
          layer: motion.departure && progress < 0.86 ? 'foreground' : destination.layer,
        }
      }
      const shuffle = this.shuffles.get(destination.id)
      if (shuffle) objects.push(...shufflePackets(object, (now - shuffle.start) / SHUFFLE_SECONDS))
      else objects.push(object)
    }
    if (!this.deals.length) return objects
    const targets = new Map(this.objects.map(object => [object.id, object]))
    this.deals = this.deals.filter(deal => targets.has(deal.target) && now < deal.start + deal.duration)
    for (const deal of this.deals) {
      if (now < deal.start) continue
      const target = targets.get(deal.target)!
      const progress = Math.max(0, Math.min(1, (now - deal.start) / deal.duration))
      // Anonymous public counts animate using only the current destination back.
      // No hidden identity, previous face, or previous private artwork is retained.
      const back = target.back ?? { title: 'Unrevealed', color: '#345b51', style: 'ornament' as const }
      objects.push({
        ...target,
        ...interpolatePose(deal.from, poseOf(target), progress, deal.lift),
        id: deal.id,
        face: back,
        back,
        interactive: false,
        inspectable: false,
        highlighted: false,
        decorative: true,
      })
    }
    return objects
  }

  get pending(): number { return this.motions.size + this.shuffles.size + this.deals.length }
  clear(): void {
    this.motions.clear()
    this.shuffles.clear()
    this.shuffleKeys.clear()
    this.zones.clear()
    this.counts.clear()
    this.deals = []
    this.serial = 0
    this.objects = []
  }
}
